import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContextType';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * ProtectedRoute component that requires authentication.
 *
 * If user is not authenticated, redirects to /login page.
 * If user is authenticated, renders the protected content.
 * Includes loading state to prevent flash of unauthenticated content.
 *
 * Usage:
 *   <Route path="/projects" element={
 *     <ProtectedRoute>
 *       <Projects />
 *     </ProtectedRoute>
 *   } />
 */
const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const [isChecking, setIsChecking] = useState(true);

  useEffect(() => {
    // Small delay to ensure auth state is properly initialized
    const timer = setTimeout(() => {
      setIsChecking(false);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // Show nothing while checking authentication (prevents flash)
  if (isChecking) {
    return null;
  }

  // Redirect to login page if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Render protected content if authenticated
  return <>{children}</>;
};

export default ProtectedRoute;
